import {
  type ChakraStylesConfig,
  type GroupBase,
  Select,
  type SingleValue,
} from 'chakra-react-select';
import type React from 'react';
import { useMemo } from 'react';

import { useSelectionLists } from '../../shared/selection-lists/hooks/useSelectionLists';
import type { SelectionList } from '../../shared/selection-lists/types';

type TaxonomyOption = { value: number | 'adaptive'; label: string };

type BaseTaxonomyCommonProps = {
  isDisabled?: boolean;
  includeCustom?: boolean;
  currentResolvedAdaptiveLabel?: string;
  variant?: 'default' | 'compact';
  usePortal?: boolean;
  customTaxonomies?: Array<{ id: number; label: string }>;
  taxonomyIds?: number[];
  selectedLabelPrefix?: string; // When provided, shows "<prefix>: <label>" in the value area
};

type BaseTaxonomyPropsWithAdaptive = BaseTaxonomyCommonProps & {
  includeAdaptive: true;
  value?: number | 'adaptive';
  onChange: (taxonomyId: number | 'adaptive') => void;
};

type BaseTaxonomyPropsWithoutAdaptive = BaseTaxonomyCommonProps & {
  includeAdaptive?: false | undefined;
  value?: number;
  onChange: (taxonomyId: number) => void;
};

export type BaseTaxonomyIdSelectProps =
  | BaseTaxonomyPropsWithAdaptive
  | BaseTaxonomyPropsWithoutAdaptive;

export const BaseTaxonomyIdSelect: React.FC<BaseTaxonomyIdSelectProps> = (
  props
) => {
  const {
    value,
    isDisabled = false,
    currentResolvedAdaptiveLabel,
    includeCustom = false,
    variant = 'default',
    usePortal = false,
    customTaxonomies = [],
    taxonomyIds,
    selectedLabelPrefix,
  } = props;
  const selectionLists = useSelectionLists(['rics_k50', 'rics_k10']);

  const options = useMemo(() => {
    type OptionType = TaxonomyOption;
    type OptionGroup = { label: string; options: OptionType[] };

    const groups: OptionGroup[] = [];

    const topOptions: OptionType[] = [];
    if (props.includeAdaptive) {
      const adaptiveLabel = currentResolvedAdaptiveLabel
        ? `Adaptive (${currentResolvedAdaptiveLabel})`
        : 'Adaptive';
      topOptions.push({ value: 'adaptive', label: adaptiveLabel });
    }
    topOptions.push({ value: 0, label: 'Default' });
    groups.push({ label: '__top__', options: topOptions });

    const buildLabel = (
      id: number,
      item:
        | SelectionList<'rics_k50'>[number]
        | SelectionList<'rics_k10'>[number]
    ) => item.longName || item.shortName || 'Unknown';

    if (customTaxonomies.length > 0 && includeCustom) {
      const customOptions: OptionType[] = customTaxonomies.map((t) => ({
        value: t.id,
        label: t.label,
      }));
      groups.push({ label: 'Custom Taxonomies', options: customOptions });
    }

    const k50Options: OptionType[] = [];
    if (selectionLists?.rics_k50) {
      selectionLists.rics_k50.forEach((item) => {
        if (!item?.id) return;
        const id = parseInt(item.id, 10);
        if (id >= 1 && id <= 50) {
          if (taxonomyIds && !taxonomyIds.includes(id)) return;
          k50Options.push({ value: id, label: buildLabel(id, item) });
        }
      });
      k50Options.sort((a, b) => (a.value as number) - (b.value as number));
    }
    if (k50Options.length > 0) {
      groups.push({ label: 'Industry K50', options: k50Options });
    }

    const k10Options: OptionType[] = [];
    if (selectionLists?.rics_k10) {
      selectionLists.rics_k10.forEach((item) => {
        if (!item?.id) return;
        const baseId = parseInt(item.id, 10);
        const id = baseId + 50;
        if (id >= 51 && id <= 60) {
          if (taxonomyIds && !taxonomyIds.includes(id)) return;
          k10Options.push({ value: id, label: buildLabel(id, item) });
        }
      });
      k10Options.sort((a, b) => (a.value as number) - (b.value as number));
    }
    if (k10Options.length > 0) {
      groups.push({ label: 'Industry K10', options: k10Options });
    }

    return groups as unknown as GroupBase<OptionType>[];
  }, [
    selectionLists,
    props.includeAdaptive,
    currentResolvedAdaptiveLabel,
    includeCustom,
    customTaxonomies,
    taxonomyIds,
  ]);

  const selectedOption = useMemo(() => {
    const flat = (options || []).flatMap((g) => g.options);
    if (value === 0) return flat.find((o) => o.value === 0) ?? null;
    if (props.includeAdaptive && value === 'adaptive') {
      return flat.find((o) => o.value === 'adaptive') ?? null;
    }
    if (typeof value === 'number') {
      return flat.find((o) => o.value === value) ?? null;
    }
    return flat.find((o) => o.value === 0) ?? null;
  }, [options, value, props.includeAdaptive]);

  type OptionType = TaxonomyOption;
  const chakraStyles: ChakraStylesConfig<
    OptionType,
    false,
    GroupBase<OptionType>
  > = {
    container: (provided) => ({
      ...provided,
      width: '100%',
      margin: 0,
    }),
    control: (provided, state) => ({
      ...provided,
      minHeight: variant === 'compact' ? '24px' : '36px',
      height: variant === 'compact' ? '24px' : '36px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: state.isFocused
        ? 'var(--chakra-colors-gray-300)'
        : 'var(--chakra-colors-gray-200)',
      backgroundColor: 'transparent',
      boxShadow: 'none !important',
      outline: '0 !important',
      ':hover': {
        borderColor: 'var(--chakra-colors-gray-300)',
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      fontSize: variant === 'compact' ? '12px' : '14px',
      paddingTop: 0,
      paddingBottom: 0,
      overflow: 'hidden',
    }),
    singleValue: (provided) => ({
      ...provided,
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      margin: 0,
    }),
    inputContainer: (provided) => ({
      ...provided,
      fontSize: variant === 'compact' ? '12px' : '14px',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: variant === 'compact' ? '24px' : '36px',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      paddingTop: 0,
      paddingBottom: 0,
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 2100,
    }),
    menuList: (provided) => ({
      ...provided,
      fontSize: variant === 'compact' ? '12px' : '14px',
    }),
    option: (provided) => ({
      ...provided,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
  };

  return (
    <Select<TaxonomyOption, false, GroupBase<TaxonomyOption>>
      isDisabled={isDisabled || !selectionLists}
      isLoading={!selectionLists}
      options={options}
      closeMenuOnScroll={false}
      blurInputOnSelect={false}
      menuShouldScrollIntoView={false}
      backspaceRemovesValue={false}
      isClearable={false}
      menuPortalTarget={
        usePortal && typeof document !== 'undefined' ? document.body : undefined
      }
      menuPosition={usePortal ? 'fixed' : 'absolute'}
      tagColorScheme="gray"
      formatOptionLabel={(opt, meta) => {
        if (meta.context === 'menu') {
          return opt.label;
        }
        if (selectedLabelPrefix) {
          return `${selectedLabelPrefix}: ${opt.label}`;
        }
        return opt.label;
      }}
      formatGroupLabel={(group) =>
        group.label === '__top__' ? null : (
          <div
            style={{
              padding: '4px 8px',
              fontSize: '12px',
              color: 'var(--chakra-colors-navyBlue-300, #6D7FA6)',
              fontWeight: 600,
            }}
          >
            {group.label}
          </div>
        )
      }
      value={selectedOption}
      onChange={(selected: SingleValue<TaxonomyOption>) => {
        if (!selected) return;
        if (props.includeAdaptive && selected.value === 'adaptive') {
          return props.onChange('adaptive');
        }
        return props.onChange(Number(selected.value));
      }}
      size="sm"
      chakraStyles={chakraStyles}
    />
  );
};
